import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useStore } from "@/lib/store";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/shop/gold", label: "Gold" },
  { to: "/shop/diamond", label: "Diamond" },
  { to: "/shop/rings", label: "Rings" },
  { to: "/shop/earrings", label: "Earrings" },
  { to: "/shop/necklaces", label: "Necklaces" },
  { to: "/shop/bangles", label: "Bangles" },
  { to: "/shop/bracelets", label: "Bracelets" },
  { to: "/shop/bridal", label: "Bridal" },
  { to: "/collections", label: "Collections" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export const BrandLockup = ({ className = "" }) => (
  <Link to="/" data-testid="brand-lockup" className={`inline-block leading-none ${className}`}>
    <div className="font-serif-luxe text-[#FAF8F5] tracking-[0.22em] text-lg sm:text-xl">
      LAXMI <span className="text-[#D4AF37]">AURA</span> JEWELS
    </div>
    <div className="text-[9px] sm:text-[10px] tracking-[0.4em] uppercase text-[#C2B29F] mt-1 italic font-light">
      By Lakshya Soni
    </div>
  </Link>
);

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { user, logout, cartCount, setCartOpen, wishlist } = useStore();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?search=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setQ("");
    }
  };

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 transition-all duration-500 ${
        scrolled ? "bg-[#12100E]/95 backdrop-blur-md shadow-[0_4px_30px_rgba(0,0,0,0.4)]" : "bg-[#12100E]/85 backdrop-blur-sm"
      } border-b border-[#D4AF37]/15`}
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex items-center justify-between h-20 sm:h-24 gap-4">
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-[#FAF8F5]"
            onClick={() => setMobileOpen(true)}
          >
            <Menu size={22} />
          </button>

          <BrandLockup className="flex-1 lg:flex-none" />

          <div className="flex items-center gap-1 sm:gap-2">
            <button data-testid="search-trigger" className="p-2.5 text-[#FAF8F5] hover:text-[#D4AF37] transition" onClick={() => setSearchOpen(true)}>
              <Search size={19} />
            </button>

            <div className="relative">
              <button data-testid="account-trigger" className="p-2.5 text-[#FAF8F5] hover:text-[#D4AF37] transition" onClick={() => setAccountOpen((v) => !v)}>
                <User size={19} />
              </button>
              {accountOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-[#1C130E] border border-[#D4AF37]/25 shadow-xl py-2 z-50">
                  {user ? (
                    <>
                      <div className="px-4 py-2 border-b border-[#D4AF37]/15">
                        <div className="text-sm text-[#FAF8F5]">{user.name}</div>
                        <div className="text-xs text-[#C2B29F]">{user.email}</div>
                      </div>
                      <Link to="/account" onClick={() => setAccountOpen(false)} className="block px-4 py-2 text-sm text-[#FAF8F5] hover:bg-[#D4AF37]/10">My Account</Link>
                      <Link to="/wishlist" onClick={() => setAccountOpen(false)} className="block px-4 py-2 text-sm text-[#FAF8F5] hover:bg-[#D4AF37]/10">My Wishlist</Link>
                      {user.is_admin && (
                        <Link to="/admin" onClick={() => setAccountOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10">
                          <LayoutDashboard size={14}/> Admin Panel
                        </Link>
                      )}
                      <button data-testid="logout-btn" onClick={() => { logout(); setAccountOpen(false); }} className="w-full text-left flex items-center gap-2 px-4 py-2 text-sm text-[#FAF8F5] hover:bg-[#D4AF37]/10">
                        <LogOut size={14}/> Sign out
                      </button>
                    </>
                  ) : (
                    <>
                      <Link to="/auth" onClick={() => setAccountOpen(false)} className="block px-4 py-2 text-sm text-[#FAF8F5] hover:bg-[#D4AF37]/10">Sign in</Link>
                      <Link to="/auth?mode=register" onClick={() => setAccountOpen(false)} className="block px-4 py-2 text-sm text-[#FAF8F5] hover:bg-[#D4AF37]/10">Create Account</Link>
                    </>
                  )}
                </div>
              )}
            </div>

            <Link to="/wishlist" data-testid="wishlist-btn" className="p-2.5 text-[#FAF8F5] hover:text-[#D4AF37] transition relative">
              <Heart size={19} />
              {wishlist.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-[#12100E] text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">
                  {wishlist.length}
                </span>
              )}
            </Link>

            <button data-testid="cart-btn" onClick={() => setCartOpen(true)} className="p-2.5 text-[#FAF8F5] hover:text-[#D4AF37] transition relative">
              <ShoppingBag size={19} />
              {cartCount > 0 && (
                <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-[#12100E] text-[10px] font-semibold rounded-full w-4 h-4 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <nav className="hidden lg:flex items-center justify-center gap-8 h-11 border-t border-[#D4AF37]/10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-[11px] tracking-[0.24em] uppercase transition gold-underline ${
                  isActive ? "text-[#D4AF37]" : "text-[#FAF8F5]/85 hover:text-[#D4AF37]"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-[#12100E]/95 backdrop-blur-md" data-testid="mobile-menu">
          <div className="p-6 flex items-center justify-between border-b border-[#D4AF37]/20">
            <BrandLockup />
            <button onClick={() => setMobileOpen(false)} className="text-[#FAF8F5]"><X size={22}/></button>
          </div>
          <nav className="p-6 flex flex-col gap-4">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setMobileOpen(false)}
                className={({ isActive }) => `text-sm tracking-[0.24em] uppercase ${isActive ? "text-[#D4AF37]" : "text-[#FAF8F5]/85"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}

      {searchOpen && (
        <div className="fixed inset-0 z-50 bg-[#12100E]/95 backdrop-blur-md flex items-start justify-center pt-32 px-6" data-testid="search-overlay">
          <div className="w-full max-w-2xl">
            <div className="eyebrow mb-4">Search Our Boutique</div>
            <form onSubmit={submitSearch}>
              <input
                data-testid="search-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search rings, necklaces, bridal…"
                className="w-full bg-transparent border-b border-[#D4AF37]/40 py-4 text-2xl font-serif-luxe text-[#FAF8F5] focus:outline-none focus:border-[#D4AF37]"
              />
            </form>
            <button onClick={() => setSearchOpen(false)} className="mt-6 text-xs tracking-[0.24em] uppercase text-[#C2B29F] hover:text-[#D4AF37]">Close</button>
          </div>
        </div>
      )}
    </header>
  );
};
